import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Fingerprint, XCircle } from 'lucide-react';
import { verifyEmailOnce } from '../../utils/emailVerificationOnce';
import { sanitizeAuthError } from '../../utils/userFacingErrors';

type VerifyState = 'working' | 'success' | 'error' | 'missing';

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const payload = useMemo(
    () => ({
      userId: params.get('userId') || '',
      secret: params.get('secret') || '',
    }),
    [params],
  );
  const [state, setState] = useState<VerifyState>(
    payload.userId && payload.secret ? 'working' : 'missing',
  );
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    if (!payload.userId || !payload.secret) return;

    let cancelled = false;
    (async () => {
      try {
        await verifyEmailOnce(payload.userId, payload.secret);
        if (!cancelled) {
          setState('success');
          setDetail('Your email is confirmed. Sign in with the same password you used when registering.');
        }
      } catch (err) {
        if (!cancelled) {
          setState('error');
          setDetail(
            sanitizeAuthError(
              err instanceof Error ? err.message : 'Could not verify this link. Request a new email from the app.',
            ),
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [payload.secret, payload.userId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4 text-white">
      <div className="grain-panel w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 shadow-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-secondary">
            {state === 'success' ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
            ) : state === 'error' || state === 'missing' ? (
              <XCircle className="h-6 w-6 text-red-400" />
            ) : (
              <Fingerprint className="h-6 w-6 animate-pulse" />
            )}
          </div>
          <h2 className="text-2xl font-bold">
            {state === 'success'
              ? 'Email verified'
              : state === 'working'
                ? 'Confirming your email…'
                : 'Verification link'}
          </h2>
          <p className="mt-2 text-sm text-white/45">
            {state === 'working' && 'One moment — finishing setup for your Pixlie account.'}
            {state === 'success' && detail}
            {state === 'error' && detail}
            {state === 'missing' &&
              'This link is incomplete. Open the latest email from Pixlie or sign in to resend verification.'}
          </p>
        </div>

        <div className="space-y-3">
          {state === 'success' && (
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full rounded-xl bg-white py-4 text-xs font-bold uppercase tracking-widest text-black hover:bg-secondary"
            >
              Continue to sign in
            </button>
          )}
          {(state === 'error' || state === 'missing') && (
            <>
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="w-full rounded-xl bg-white py-4 text-xs font-bold uppercase tracking-widest text-black hover:bg-secondary"
              >
                Back to sign in
              </button>
              <button
                type="button"
                onClick={() => navigate('/register', { replace: true })}
                className="w-full rounded-xl border border-white/15 py-4 text-xs font-bold uppercase tracking-widest text-white/80 hover:border-secondary"
              >
                Create account
              </button>
            </>
          )}
          <p className="text-center text-xs text-white/35">
            <Link to="/" className="text-secondary hover:underline">
              Back to home
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
